import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';
import { getPatientById, getPatienttsNameList } from './patientSlice';

// Helper function to extract serializable error data from AxiosError
const extractErrorData = (error) => {
    if (!error) return null;
    
    // If it's already a plain object (not an AxiosError), return it as is
    if (typeof error === 'object' && !error.isAxiosError && !error.response) {
        return error;
    }
    
    // Extract serializable data from AxiosError
    if (error.isAxiosError || error.response) {
        const responseData = error.response?.data;
        return {
            message: responseData?.message || error.message || 'An error occurred',
            status: error.response?.status || null,
            data: responseData || null,
            code: error.code || null
        };
    }
    
    // If it's a string, return it
    if (typeof error === 'string') {
        return error;
    }
    
    // Default fallback
    return { message: error?.message || 'An error occurred' };
};

// Book an appointment
export const bookAppointment = createAsyncThunk(
    'appointment/bookAppointment',
    async (payload, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        const updatedPayload = {
            ...payload,
            appointmentBy: userRole,
            role: userRole,
          };
        try {
            const response = await axiosInstance.post(`${baseURL}/appointment/book`, updatedPayload);
            const jsonResponse = response.data;
            dispatch(getAllAppointments({ 
                doctorId: payload.doctorId,
                start: payload.start,
                end: payload.end
            }));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Book an appointment with new patient
export const bookWitthNewPatient = createAsyncThunk(
    'appointment/bookWitthNewPatient',
    async (payload, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        const updatedPayload = {
            ...payload,
            appointmentBy: userRole,
            role: userRole,
          };
        try {
            const response = await axiosInstance.post(`${baseURL}/appointment/bookNew`, updatedPayload);
            const jsonResponse = response.data;
            dispatch(getAllAppointments({ 
                doctorId: payload.doctorId,
                start: payload.start,
                end: payload.end
            }));
            dispatch(getPatienttsNameList(payload.doctorId));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Fetch single appointment
export const getAppointmentById = createAsyncThunk(
    'appointment/getAppointmentById',
    async (appointmentId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/appointment/${appointmentId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Fetch the AI meeting transcript + summary for an appointment (Therapeu-Ai
// result, stored via the /api/webhook/ai webhook). 404 = not generated yet.
export const getAppointmentTranscript = createAsyncThunk(
    'appointment/getAppointmentTranscript',
    async (appointmentId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/appointment/transcript/${appointmentId}`);
            return response.data?.data || null;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Generate ONE overall summary across the patient's appointments handled by the
// logged-in doctor. Body: { patientId, template, filters }. Returns
// { summary, template, count }.
export const generatePatientOverallSummary = createAsyncThunk(
    'appointment/generatePatientOverallSummary',
    async ({ patientId, doctorId, template, filters }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(
                `${baseURL}/appointment/patient-overall-summary`,
                { patientId, doctorId, template, filters },
            );
            return response.data?.data || null;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Poll the AI summary + whether inputs are still generating (for the loader).
// Returns { summary, pending, status }.
export const getAppointmentSummary = createAsyncThunk(
    'appointment/getAppointmentSummary',
    async (appointmentId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/appointment/${appointmentId}/summary`);
            return response.data?.data || null;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Translate the appointment AI summary into another language (backend caches it).
// Returns { summary, lang }.
export const translateAppointmentSummary = createAsyncThunk(
    'appointment/translateAppointmentSummary',
    async ({ appointmentId, lang, languageName }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(
                `${baseURL}/appointment/${appointmentId}/summary-translate`,
                { lang, languageName }
            );
            return response.data?.data || null;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Fetch all appointments for a doctor
export const getAllAppointments = createAsyncThunk(
    'appointment/getAllAppointments',
    async ({ doctorId, start, end }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const params = { timezone };
            if (start) params.start = start;
            if (end) params.end = end;
            
            const response = await axiosInstance.get(`${baseURL}/appointment/doctor/${doctorId}`, {
                params
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Fetch rescheduling-recommendation slots for a doctor in a date range
export const getReschedulingSlotsByDoctor = createAsyncThunk(
    'appointment/getReschedulingSlotsByDoctor',
    async ({ doctorId, start, end }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = {};
            if (start) params.start = start;
            if (end) params.end = end;
            const response = await axiosInstance.get(
                `${baseURL}/appointment/rescheduling-slots/${doctorId}`,
                { params }
            );
            return response.data?.data || [];
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Save appointment via update endpoint (single API for create / edit / new-patient)
export const saveAppointmentUpdate = createAsyncThunk(
    'appointment/saveAppointmentUpdate',
    async ({ appointmentId, payload, isNewPatient }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        const updatedPayload = {
            ...payload,
            appointmentStatus: 'Booked',
            appointmentBy: userRole,
            role: userRole,
        };
        try {
            const response = await axiosInstance.put(
                `${baseURL}/appointment/update/${appointmentId}`,
                updatedPayload
            );
            const jsonResponse = response.data;
            dispatch(getAllAppointments({
                doctorId: payload.doctorId,
                start: payload.start,
                end: payload.end,
            }));
            if (isNewPatient) {
                dispatch(getPatienttsNameList(payload.doctorId));
            }
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Reschedule appointment
export const rescheduleAppointment = createAsyncThunk(
    'appointment/rescheduleAppointment',
    async (payload, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        const updatedPayload = { ...payload, role: userRole };
        try {
            const response = await axiosInstance.post(`${baseURL}/appointment/rescheduleAppointment`, updatedPayload);
            const jsonResponse = response.data;
            dispatch(getAllAppointments({ 
                doctorId: payload.doctorId,
                start: payload.start,
                end: payload.end
            }));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Cancel appointment
export const cancelAppointment = createAsyncThunk(
    'appointment/cancelAppointment',
    async ({ appointmentId, doctorId, start, end }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        try {
            const response = await axiosInstance.post(`${baseURL}/appointment/cancel/${appointmentId}`, { role: userRole });
            const jsonResponse = response.data;
            if (doctorId) {
                dispatch(getAllAppointments({ 
                    doctorId,
                    start,
                    end
                }));
            }
            return { appointmentId };
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Update doctor notes
export const updateDoctorNotes = createAsyncThunk(
    'appointment/updateDoctorNotes',
    async ({ appointmentId, doctorId, description }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/appointment/notes/${doctorId}/${appointmentId}`, { description: description });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const updateDoctorNotes1 = createAsyncThunk(
    'appointment/updateDoctorNotes1',
    async ({ appointmentId, doctorId, notes }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/appointment/notes/${doctorId}/${appointmentId}`, { doctorDescription: notes });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Update preparation notes
export const updatePreparationNotes = createAsyncThunk(
    'appointment/updatePreparationNotes',
    async ({ appointmentId, notes }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`${baseURL}/appointment/${appointmentId}/preparation-notes`, { preparationNotes: notes });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Update written notes
export const updateWrittenNotes = createAsyncThunk(
    'appointment/updateWrittenNotes',
    async ({ appointmentId, notes }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`${baseURL}/appointment/writtenNotes/${appointmentId}`, { writtenNotes: notes });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Update summary notes
export const updateSummaryNotes = createAsyncThunk(
    'appointment/updateSummaryNotes',
    async ({ appointmentId, notes }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`${baseURL}/appointment/${appointmentId}/summary-notes`, { summaryNotes: notes });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Upload audio
export const uploadAudio = createAsyncThunk(
    "appointment/uploadAudio",
    async ({ appointmentId, audio }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {

            const formData = new FormData();
            formData.append("audioFile", audio);

            const response = await axiosInstance.post(
                `${baseURL}/uploadAudio/${appointmentId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || error
            );
        }
    }
);

// Delete uploaded audio (by uploadId — mirrors deleteUploadedImage)
export const deleteAudio = createAsyncThunk(
    'appointment/deleteAudio',
    async ({ appointmentId, uploadId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/deleteAudio/${appointmentId}/${uploadId}`);
            return { ...response.data, uploadId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error);
        }
    }
);

// Update transcribed notes
export const updateTranscribedNotes = createAsyncThunk(
    'appointment/updateTranscribedNotes',
    async ({ appointmentId, notes }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`${baseURL}/appointment/updatetranscribedNotes/${appointmentId}`, { notes });
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Upload image
export const uploadImage = createAsyncThunk(
    'appointment/uploadImage',
    async ({ appointmentId, image }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('imageFile', image);

            const response = await axiosInstance.post(
                `${baseURL}/uploadImage/${appointmentId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

// Delete uploaded image
export const deleteUploadedImage = createAsyncThunk(
    'appointment/deleteUploadedImage',
    async ({ appointmentId, uploadId }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`${baseURL}/deleteImage/${appointmentId}/${uploadId}`);
            return { ...response.data, uploadId };
        } catch (error) {
            return rejectWithValue(error.response?.data);
        }
    }
);

// Update OCR notes
export const updateOcrNotes = createAsyncThunk(
    'appointment/updateOcrNotes',
    async ({ appointmentId, notes }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`${baseURL}/appointment/updateocrnotes/${appointmentId}`, { notes });
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Update appointment (status, payment, etc.)
export const updateAppointment = createAsyncThunk(
    'appointment/updateAppointment',
    async ({ appointmentId, method, paymentStatus, date, appointmentStatus, start, end }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        try {
            const response = await axiosInstance.put(
                `${baseURL}/appointment/update/${appointmentId}`,
                { method, paymentStatus, date, appointmentStatus, role: userRole }
            );
            const jsonResponse = response.data;
            if (appointmentStatus === 'NoShow') {
                dispatch(getAllAppointments({ 
                    doctorId: jsonResponse?.appointment?.doctorId,
                    start,
                    end
                }));
            } else {
                dispatch(getPatientById(jsonResponse?.appointment?.patientId));
                dispatch(getAllAppointments({ 
                    doctorId: jsonResponse?.appointment?.doctorId,
                    start,
                    end
                }));
            }
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Fetch available appointment slots
export const getAvailableSlots = createAsyncThunk(
    'appointment/getAvailableSlots',
    async ({ doctorId, startDate, endDate, timeZone, addressType, appointmentTypeId, duration, isReschedulingProcess, appointmentId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { startDate, endDate, timeZone, addressType, appointmentTypeId };
            if (duration !== undefined && duration !== null && duration !== '') params.duration = duration;
            if (isReschedulingProcess) params.isReschedulingProcess = 'true';
            if (appointmentId) params.appointmentId = appointmentId;
            const response = await axiosInstance.get(`${baseURL}/appointment/${doctorId}/availableSlots`, {
                params
            });
            return response.data.availableSlots;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Fetch available appointment slots
export const getDoctorAvailableSlots = createAsyncThunk(
    'appointment/getDoctorAvailableSlots',
    async ({ doctorId, startDate, endDate, timeZone, addressType, appointmentTypeId, duration, isReschedulingProcess, appointmentId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = { startDate, endDate, timeZone, addressType, appointmentTypeId };
            if (duration !== undefined && duration !== null && duration !== '') params.duration = duration;
            if (isReschedulingProcess) params.isReschedulingProcess = 'true';
            if (appointmentId) params.appointmentId = appointmentId;
            // Role so the backend can apply the doctor/admin past-date opt-out
            // (never relaxed for patients). Default behavior is unchanged.
            params.appointmentBy = localStorage.getItem('Role') || undefined;
            const response = await axiosInstance.get(`${baseURL}/appointment/${doctorId}/availableSlotsWithSettings`, {
                params
            });
            return response.data.availableSlots;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Add new async thunk for filtering appointments
export const filterAppointments = createAsyncThunk(
    'appointment/filterAppointments',
    async ({ doctorId, startDate, endDate, paymentMethod, appointmentStatus, patientId, appointmentId, page, limit, searchTerm,SpecialtyId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        console.log('SpecialtyId', SpecialtyId)
        try {
            const response = await axiosInstance.get(`${baseURL}/appointment/filter/appoint`, {
                params: { doctorId, startDate, endDate, paymentMethod, appointmentStatus, patientId,speId:SpecialtyId, appointmentId, page, limit, searchTerm }
            });
            const jsonResponse = response.data; // Return full response with metadata
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Clear all appointments for a doctor on a specific day
export const cancelAllAppointments = createAsyncThunk(
    'appointment/clearAllAppointments',
    async ({ doctorId, day, days, start, end }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            // Build payload - use day key for both single and multiple days
            const payload = {
                doctorId,
                day: (days && Array.isArray(days) && days.length > 0) ? days : day
            };
            
            const response = await axiosInstance.post(`${baseURL}/doctors/cancelavailability`, payload);
            const jsonResponse = response.data;
            // Refresh the appointments list after clearing
            dispatch(getAllAppointments({ 
                doctorId,
                start,
                end
            }));
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Cancel all appointments for leave dates
export const LeaveCancelAllAppointment = createAsyncThunk(
    'appointment/LeaveCancelAllAppointment',
    async ({ doctorId, startDate, endDate }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(
                `${baseURL}/appointment/cancellAllAppoit`,
                {},
                {
                    params: { doctorId, startDate, endDate }
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Send reschedule recommendation
export const sendRescheduleRecommendation = createAsyncThunk(
    'appointment/sendRescheduleRecommendation',
    async ({ appointmentId, payload }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        const userRole = localStorage.getItem('Role');
        const updatedPayload = { ...payload, role: userRole };
        try {
            const response = await axiosInstance.put(
                `${baseURL}/appointment/reschedule-recommendation-appointment/${appointmentId}`,
                updatedPayload
            );
            const jsonResponse = response.data;
            return jsonResponse;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Get reschedule recommendation
export const getRescheduleRecommendation = createAsyncThunk(
    'appointment/getRescheduleRecommendation',
    async ({ patientId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/appointment/list`, {
                params: { patientId, status }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Get available doctors by specialty and date
export const getAvailableDoctor = createAsyncThunk(
    'appointment/getAvailableDoctor',
    async ({ specialtyId, startDate, patientId, endDate='', countryId = '', page = 1, limit = 10 }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ specialtyId, startDate, endDate });
            if (patientId) params.append('patientId', patientId);
            if (countryId) params.append('countryId', countryId);
            // if (page) params.append('page', page);
            // if (limit) params.append('limit', limit);
            const response = await axiosInstance.get(`${baseURL}/doctors/available-by-specialty?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Get single day available slots
export const getSingleDaySlots = createAsyncThunk(
    'appointment/getSingleDaySlots',
    async ({ doctorId, date, timeZone, specialtyId, appointmentTypeId, addressId, duration, isReschedulingProcess }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ date, timeZone });
            if (specialtyId) params.append('SpecialtyId', specialtyId);
            if (appointmentTypeId) params.append('appointmentTypeId', appointmentTypeId);
            if (addressId) params.append('addressId', addressId);
            if (duration !== undefined && duration !== null && duration !== '') params.append('duration', duration);
            if (isReschedulingProcess) params.append('isReschedulingProcess', 'true');
            const response = await axiosInstance.get(`${baseURL}/appointment/${doctorId}/availableSlotsNew?${params.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractErrorData(error));
        }
    }
);

// Slice
const appointmentSlice = createSlice({
    name: 'appointment',
    initialState: {
        appointments: [],
        payments: [],
        paymentsMetadata: { total: 0, page: 1, limit: 10, totalPages: 0 },
        selectedAppointment: null,
        availableSlots: [],
        availableDoctorSlots: [],
        availableDoctors: [],
        availableDoctorsMetadata: { total: 0, page: 1, limit: 10, totalPages: 0 },
        singleDaySlots: null,
        isSingleDaySlotsLoading: false,
        controlDuration: null,
        earlyOpenRestriction: true,
        isLoading: false,
        isAppointmentLoading: false,
        allAppointmentsLoading: false,
        isBookNewPatientLoading: false,
        isSaveAppointmentLoading: false,
        isAvailableDoctorsLoading: false,
        error: null,
        cancelError: null,
    },
    reducers: {
        clearAllAppointments: (state) => {
            state.appointments = [];
            state.payments = [];
            state.selectedAppointment = null;
            state.availableSlots = [];
            state.availableDoctorSlots= [],
            state.controlDuration = null;
            state.earlyOpenRestriction = true;
            state.isLoading = false;
            state.isAppointmentLoading = false;
            state.allAppointmentsLoading = false;
            state.isBookNewPatientLoading = false;
            state.error = null;
            state.cancelError = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        // Apply an async transcription / OCR result pushed over Socket.IO onto
        // the matching audioUploads / imageUploads item of the selected appt.
        applyMediaResult: (state, action) => {
            const { appointmentId, uploadId, kind, text, status } = action.payload || {};
            const appt = state.selectedAppointment;
            if (!appt || (appointmentId && appt.appointmentId !== appointmentId)) return;
            const arrayKey = kind === 'audio' ? 'audioUploads' : 'imageUploads';
            const field = kind === 'audio' ? 'transcribedNotes' : 'ocrNotes';
            const arr = appt[arrayKey];
            if (!Array.isArray(arr)) return;
            const item = arr.find((u) => u && u.uploadId === uploadId);
            if (item) {
                item[field] = text || '';
                item.status = status || 'done';
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Book Appointment
            .addCase(bookAppointment.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(bookAppointment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.appointments = [...state.appointments, action.payload.data || action.payload];
            })
            .addCase(bookAppointment.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to book appointment';
            })
            // Book Appointment With New Patient
            .addCase(bookWitthNewPatient.pending, (state) => {
                state.isBookNewPatientLoading = true;
                state.error = null;
            })
            .addCase(bookWitthNewPatient.fulfilled, (state, action) => {
                state.isBookNewPatientLoading = false;
                state.appointments = [...state.appointments, action.payload.data || action.payload];
            })
            .addCase(bookWitthNewPatient.rejected, (state, action) => {
                state.isBookNewPatientLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to book appointment with new patient';
            })
            // Get Appointment By ID
            .addCase(getAppointmentById.pending, (state) => {
                state.isAppointmentLoading = true;
                state.error = null;
            })
            .addCase(getAppointmentById.fulfilled, (state, action) => {
                state.isAppointmentLoading = false;
                state.selectedAppointment = action.payload;
            })
            .addCase(getAppointmentById.rejected, (state, action) => {
                state.isAppointmentLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to fetch appointment';
            })
            // Get All Appointments
            .addCase(getAllAppointments.pending, (state) => {
                state.allAppointmentsLoading = true;
                state.error = null;
            })
            .addCase(getAllAppointments.fulfilled, (state, action) => {
                state.allAppointmentsLoading = false;
                state.appointments = action.payload?.data?.appointmentsWithEndTimes
                    || action.payload?.appointmentsWithEndTimes;
                state.controlDuration = action.payload?.data?.ctrlDuration || action.payload?.ctrlDuration ;
                state.earlyOpenRestriction = action.payload?.data?.earlyOpenRestriction ?? action.payload?.earlyOpenRestriction ?? true;
            })
            .addCase(getAllAppointments.rejected, (state, action) => {
                state.allAppointmentsLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to fetch appointments';
            })
            // Reschedule Appointment
            .addCase(rescheduleAppointment.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(rescheduleAppointment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.appointments = state.appointments.map(appointment =>
                    appointment?._id === action.payload?.data?._id ? action.payload?.data : appointment
                );
                state.selectedAppointment = action.payload?.data || action.payload;
            })
            .addCase(rescheduleAppointment.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to reschedule appointment';
            })
            // Cancel Appointment
            .addCase(cancelAppointment.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(cancelAppointment.fulfilled, (state, action) => {
                state.isLoading = false;
                const cancelledId = action.payload.appointmentId;
                state.appointments = state.appointments.filter(a => a._id !== cancelledId);
                if (state.selectedAppointment?._id === cancelledId) {
                    state.selectedAppointment = null;
                }
            })
            .addCase(cancelAppointment.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to cancel appointment';
            })
            // Update Doctor Notes
            .addCase(updateDoctorNotes.pending, (state) => {
                state.error = null;
            })
            .addCase(updateDoctorNotes.fulfilled, (state, action) => {
                const appt = action.payload?.data || action.payload?.appointment || action.payload?.updatedAppointment;
                if (appt && state.selectedAppointment && appt.description !== undefined) {
                    state.selectedAppointment.description = appt.description;
                }
            })
            .addCase(updateDoctorNotes.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to update doctor notes';
            })
            // Update Doctor Notes
            .addCase(updateDoctorNotes1.pending, (state) => {
                state.error = null;
            })
            .addCase(updateDoctorNotes1.fulfilled, (state, action) => {
                const appt = action.payload?.data || action.payload?.appointment || action.payload?.updatedAppointment;
                if (appt && state.selectedAppointment && appt.doctorDescription !== undefined) {
                    state.selectedAppointment.doctorDescription = appt.doctorDescription;
                }
            })
            .addCase(updateDoctorNotes1.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to update doctor notes';
            })
            // Update Preparation Notes
            .addCase(updatePreparationNotes.pending, (state) => {
                state.error = null;
            })
            .addCase(updatePreparationNotes.fulfilled, (state, action) => {
                const appt = action.payload?.data || action.payload?.appointment || action.payload?.updatedAppointment;
                if (appt && state.selectedAppointment && appt.preparationNotes !== undefined) {
                    state.selectedAppointment.preparationNotes = appt.preparationNotes;
                }
            })
            .addCase(updatePreparationNotes.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to update preparation notes';
            })
            // Update Written Notes
            .addCase(updateWrittenNotes.pending, (state) => {
                state.error = null;
            })
            .addCase(updateWrittenNotes.fulfilled, (state, action) => {
                const appt = action.payload?.data || action.payload?.appointment || action.payload?.updatedAppointment;
                if (appt && state.selectedAppointment && appt.writtenNotes !== undefined) {
                    state.selectedAppointment.writtenNotes = appt.writtenNotes;
                }
            })
            .addCase(updateWrittenNotes.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to update written notes';
            })
            // Update Summary Notes
            .addCase(updateSummaryNotes.pending, (state) => {
                state.error = null;
            })
            .addCase(updateSummaryNotes.fulfilled, (state, action) => {
                const appt = action.payload?.data || action.payload?.appointment || action.payload?.updatedAppointment;
                if (appt && state.selectedAppointment && appt.summaryNotes !== undefined) {
                    state.selectedAppointment.summaryNotes = appt.summaryNotes;
                }
            })
            .addCase(updateSummaryNotes.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to update summary notes';
            })
            // Upload Audio
            .addCase(uploadAudio.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadAudio.fulfilled, (state, action) => {
                state.isLoading = false;
                if (state.selectedAppointment && action.payload) {
                    // Audio upload returns { filePath, transcription, audioUploads }
                    if (action.payload.filePath) {
                        state.selectedAppointment.voiceClipUrl = action.payload.filePath;
                    }
                    if (action.payload.transcription !== undefined) {
                        state.selectedAppointment.transcribedNotes = action.payload.transcription;
                    }
                    if (action.payload.audioUploads) {
                        state.selectedAppointment.audioUploads = action.payload.audioUploads;
                    }
                }
            })
            .addCase(uploadAudio.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to upload audio';
            })
            .addCase(deleteAudio.fulfilled, (state, action) => {
                if (state.selectedAppointment) {
                    const next = action.payload?.audioUploads;
                    if (next) {
                        state.selectedAppointment.audioUploads = next;
                        const last = next.length > 0 ? next[next.length - 1] : null;
                        state.selectedAppointment.voiceClipUrl = last ? last.audioUrl : '';
                        state.selectedAppointment.transcribedNotes = last ? last.transcribedNotes : '';
                    } else {
                        state.selectedAppointment.voiceClipUrl = '';
                        state.selectedAppointment.transcribedNotes = '';
                    }
                }
            })
            .addCase(deleteAudio.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to delete audio';
            })
            // Update Transcribed Notes
            .addCase(updateTranscribedNotes.pending, (state) => {
                state.error = null;
            })
            .addCase(updateTranscribedNotes.fulfilled, (state, action) => {
                const appt = action.payload?.data || action.payload?.appointment || action.payload?.updatedAppointment;
                if (appt && state.selectedAppointment && appt.transcribedNotes !== undefined) {
                    state.selectedAppointment.transcribedNotes = appt.transcribedNotes;
                }
            })
            .addCase(updateTranscribedNotes.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to update transcribed notes';
            })
            // Upload Image
            .addCase(uploadImage.pending, (state) => {
                state.error = null;
            })
            .addCase(uploadImage.fulfilled, (state, action) => {
                if (state.selectedAppointment && action.payload) {
                    // Image upload returns { imageUploads, imageUrl, uploadId, ... }
                    if (action.payload.imageUploads) {
                        state.selectedAppointment.imageUploads = action.payload.imageUploads;
                    }
                    if (action.payload.imageUrl) {
                        state.selectedAppointment.imageUrl = action.payload.imageUrl;
                    }
                    if (action.payload.extractedText !== undefined) {
                        state.selectedAppointment.ocrNotes = action.payload.extractedText;
                    }
                }
            })
            .addCase(uploadImage.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to upload image';
            })
            // Delete Uploaded Image
            .addCase(deleteUploadedImage.fulfilled, (state, action) => {
                if (state.selectedAppointment && action.payload) {
                    if (action.payload.imageUploads) {
                        state.selectedAppointment.imageUploads = action.payload.imageUploads;
                    }
                    // Update legacy fields
                    if (action.payload.imageUploads?.length > 0) {
                        const last = action.payload.imageUploads[action.payload.imageUploads.length - 1];
                        state.selectedAppointment.imageUrl = last.imageUrl;
                        state.selectedAppointment.ocrNotes = last.ocrNotes;
                    } else {
                        state.selectedAppointment.imageUrl = '';
                        state.selectedAppointment.ocrNotes = '';
                    }
                }
            })
            // Update OCR Notes
            .addCase(updateOcrNotes.pending, (state) => {
                state.error = null;
            })
            .addCase(updateOcrNotes.fulfilled, (state, action) => {
                const appt = action.payload?.data || action.payload?.appointment || action.payload?.updatedAppointment;
                if (appt && state.selectedAppointment && appt.ocrNotes !== undefined) {
                    state.selectedAppointment.ocrNotes = appt.ocrNotes;
                }
            })
            .addCase(updateOcrNotes.rejected, (state, action) => {
                state.error = extractErrorData(action.payload) || 'Failed to update OCR notes';
            })
            // Save Appointment via Update endpoint
            .addCase(saveAppointmentUpdate.pending, (state) => {
                state.isSaveAppointmentLoading = true;
                state.error = null;
            })
            .addCase(saveAppointmentUpdate.fulfilled, (state, action) => {
                state.isSaveAppointmentLoading = false;
                state.selectedAppointment = action.payload.data || action.payload;
                state.appointments = state.appointments.map(appointment =>
                    appointment._id === (action.payload.data?._id || action.payload?._id)
                        ? (action.payload.data || action.payload)
                        : appointment
                );
            })
            .addCase(saveAppointmentUpdate.rejected, (state, action) => {
                state.isSaveAppointmentLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to save appointment';
            })
            // Update Appointment
            .addCase(updateAppointment.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateAppointment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedAppointment = action.payload.data || action.payload;
                state.appointments = state.appointments.map(appointment =>
                    appointment._id === action.payload.data?._id ? action.payload.data : appointment
                );
            })
            .addCase(updateAppointment.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to update appointment';
            })
            // Get Available Slots
            .addCase(getAvailableSlots.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAvailableSlots.fulfilled, (state, action) => {
                state.isLoading = false;
                state.availableSlots = action.payload.data || action.payload;
            })
            .addCase(getAvailableSlots.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to fetch available slots';
            })
            // Get Available Slots
            .addCase(getDoctorAvailableSlots.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getDoctorAvailableSlots.fulfilled, (state, action) => {
                state.isLoading = false;
                state.availableDoctorSlots = action.payload.data || action.payload;
            })
            .addCase(getDoctorAvailableSlots.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to fetch available slots';
            })
            // Filter Appointments
            .addCase(filterAppointments.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(filterAppointments.fulfilled, (state, action) => {
                state.isLoading = false;
                state.payments = action.payload.appointments || action.payload; // Handle both formats
                state.paymentsMetadata = {
                    total: action.payload.total || 0,
                    page: action.payload.page || 1,
                    limit: action.payload.limit || 10,
                    totalPages: action.payload.totalPages || 0
                };
            })
            .addCase(filterAppointments.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to filter appointments';
            })
            // Clear All Appointments
            .addCase(cancelAllAppointments.pending, (state) => {
                state.isLoading = true;
                state.cancelError = null;
            })
            .addCase(cancelAllAppointments.fulfilled, (state, action) => {
                state.isLoading = false;
                // state.appointments = [];
                state.selectedAppointment = null;
            })
            .addCase(cancelAllAppointments.rejected, (state, action) => {
                state.isLoading = false;
                state.cancelError = extractErrorData(action.payload) || 'Failed to clear all appointments';
            })
            // Leave Cancel All Appointments
            .addCase(LeaveCancelAllAppointment.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(LeaveCancelAllAppointment.fulfilled, (state, action) => {
                state.isLoading = false;
            })
            .addCase(LeaveCancelAllAppointment.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to cancel all appointments for leave';
            })
            // Send Reschedule Recommendation
            .addCase(sendRescheduleRecommendation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(sendRescheduleRecommendation.fulfilled, (state, action) => {
                state.isLoading = false;
            })
            .addCase(sendRescheduleRecommendation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to send reschedule recommendation';
            })
            // Get Reschedule Recommendation
            .addCase(getRescheduleRecommendation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getRescheduleRecommendation.fulfilled, (state, action) => {
                state.isLoading = false;
            })
            .addCase(getRescheduleRecommendation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to get reschedule recommendation';
            })
            // Get Available Doctors
            .addCase(getAvailableDoctor.pending, (state) => {
                state.isAvailableDoctorsLoading = true;
                state.error = null;
            })
            .addCase(getAvailableDoctor.fulfilled, (state, action) => {
                state.isAvailableDoctorsLoading = false;
                state.availableDoctors = action.payload?.doctors || action.payload?.data?.doctors || action.payload?.data || [];
                state.availableDoctorsMetadata = {
                    total: action.payload?.total || action.payload?.data?.total || 0,
                    page: action.payload?.page || action.payload?.data?.page || 1,
                    limit: action.payload?.limit || action.payload?.data?.limit || 10,
                    totalPages: action.payload?.totalPages || action.payload?.data?.totalPages || 0
                };
            })
            .addCase(getAvailableDoctor.rejected, (state, action) => {
                state.isAvailableDoctorsLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to fetch available doctors';
            })
            // Get Single Day Slots
            .addCase(getSingleDaySlots.pending, (state) => {
                state.isSingleDaySlotsLoading = true;
                state.error = null;
            })
            .addCase(getSingleDaySlots.fulfilled, (state, action) => {
                state.isSingleDaySlotsLoading = false;
                state.singleDaySlots = action.payload?.data || action.payload;
            })
            .addCase(getSingleDaySlots.rejected, (state, action) => {
                state.isSingleDaySlotsLoading = false;
                state.error = extractErrorData(action.payload) || 'Failed to fetch available slots';
            });
    },
});

export const { clearAllAppointments, clearError, applyMediaResult } = appointmentSlice.actions;
export default appointmentSlice.reducer;